import * as THREE from "three";
import { DIGIT_CELL, LETTER_CELL, MINUS_CELL, PLUS_CELL, glyphTexture } from "./enemyGlyphs";
import { MARKS_FRAGMENT, MARKS_VERTEX } from "./enemyMarksShader";

/** Floats per instance (four vec4s, see enemyMarksShader.ts). */
const STRIDE = 16;
/** Floating numbers alive at once, and characters per number. */
const FLOATERS = 24;
const FLOATER_CHARS = 6;   // M36: IMMUNE
/** A floating number rises this many CSS px over its life, and fades over the last part of it. */
const FLOAT_RISE_PX = 30;
const FLOAT_FADE_FROM = 0.55;
/** Character advance as a fraction of the glyph size (the digits are drawn narrower than their cell). */
const CHAR_ADVANCE = 0.56;
/** Drawn last and on top: the marks are read like HUD, never hidden behind a tower. */
const PLATE_RENDER_ORDER = 1000;
/** Same order as the old blob shadows: after the ground, before the units. */
const GROUND_RENDER_ORDER = 1;

interface Floater { active: boolean; x: number; y: number; z: number; text: string; r: number; g: number; b: number; size: number; age: number; life: number }

/** One instanced layer: an interleaved buffer that uploads only the live range, and nothing while empty. */
class Layer {
  readonly mesh: THREE.Mesh;
  readonly data: Float32Array;
  private readonly buffer: THREE.InstancedInterleavedBuffer;
  private readonly geo: THREE.InstancedBufferGeometry;
  n = 0;
  private drawn = 0;

  constructor(readonly capacity: number, material: THREE.ShaderMaterial, renderOrder: number) {
    this.data = new Float32Array(capacity * STRIDE);
    this.buffer = new THREE.InstancedInterleavedBuffer(this.data, STRIDE, 1).setUsage(THREE.DynamicDrawUsage);
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.setAttribute("position", quad.getAttribute("position"));
    for (let i = 0; i < 4; i++) this.geo.setAttribute(`a${i}`, new THREE.InterleavedBufferAttribute(this.buffer, 4, i * 4));
    this.geo.instanceCount = 0;
    this.mesh = new THREE.Mesh(this.geo, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
  }

  /** The next instance's offset into `data`, or -1 when full. */
  next(): number { return this.n < this.capacity ? (this.n++) * STRIDE : -1; }

  commit(): void {
    const n = this.n;
    if (n > 0 || this.drawn > 0) {
      this.buffer.clearUpdateRanges();
      if (n > 0) { this.buffer.addUpdateRange(0, n * STRIDE); this.buffer.needsUpdate = true; }
    }
    this.drawn = n;
    this.geo.instanceCount = n;
    this.mesh.visible = n > 0;
  }

  dispose(): void { this.geo.dispose(); }
}

/**
 * Every enemy's type badge, HP bar, status icons and ground ring — and the floating kill/leak numbers — in two draw
 * calls and one program, whatever the unit count. This replaced a mesh per HP bar and a mesh per contact shadow (a
 * draw each per unit). Per frame the views write each live unit's instance and one buffer range goes up per layer;
 * the glyphs are one atlas texture uploaded once.
 */
export class EnemyMarks {
  readonly group = new THREE.Group();
  private readonly plates: Layer;
  private readonly ground: Layer;
  private readonly floaters: Floater[] = Array.from({ length: FLOATERS }, () => ({ active: false, x: 0, y: 0, z: 0, text: "", r: 1, g: 1, b: 1, size: 16, age: 0, life: 1 }));
  private readonly px = { value: new THREE.Vector2(0.005, 0.0025) };
  private static readonly size = new THREE.Vector2();

  constructor(units: number) {
    const atlas = { value: glyphTexture() };
    const make = (ground: boolean) => new THREE.ShaderMaterial({
      vertexShader: MARKS_VERTEX, fragmentShader: MARKS_FRAGMENT,
      uniforms: { uAtlas: atlas, uPx: this.px, uGround: { value: ground ? 1 : 0 } },
      transparent: true, depthWrite: false, depthTest: ground, toneMapped: false,
    });
    this.plates = new Layer(units + FLOATERS * FLOATER_CHARS, make(false), PLATE_RENDER_ORDER);
    this.ground = new Layer(units * 2, make(true), GROUND_RENDER_ORDER);   // a ring each, and an aura for some
    // Screen-space sizes are in CSS pixels: the canvas's CSS size, read as the plates are drawn.
    this.plates.mesh.onBeforeRender = (renderer) => {
      renderer.getSize(EnemyMarks.size);
      this.px.value.set(2 / Math.max(1, EnemyMarks.size.x), 2 / Math.max(1, EnemyMarks.size.y));
    };
    this.group.add(this.ground.mesh, this.plates.mesh);
  }

  /** Starts a frame's instances. */
  begin(): void { this.plates.n = 0; this.ground.n = 0; }

  /** A unit's overhead plate: `hp` −1 hides the bar; status cells −1 for none. `colour` is linear RGB. */
  plate(x: number, y: number, z: number, roleCell: number, hp: number, s1: number, s2: number, colour: THREE.Color, alpha: number, radius: number, boss: boolean): void {
    const o = this.plates.next();
    if (o < 0) return;
    const d = this.plates.data;
    d[o] = x; d[o + 1] = y; d[o + 2] = z; d[o + 3] = 0;
    d[o + 4] = roleCell; d[o + 5] = hp; d[o + 6] = s1; d[o + 7] = s2;
    d[o + 8] = colour.r; d[o + 9] = colour.g; d[o + 10] = colour.b; d[o + 11] = alpha;
    d[o + 12] = radius; d[o + 13] = boss ? 1 : 0; d[o + 14] = 0; d[o + 15] = 0;
  }

  /** A unit's ground ring and contact shadow, `size` world units across. */
  ring(x: number, y: number, z: number, size: number, colour: THREE.Color, alpha: number, air: boolean): void {
    const o = this.ground.next();
    if (o < 0) return;
    const d = this.ground.data;
    d[o] = x; d[o + 1] = y; d[o + 2] = z; d[o + 3] = 0;
    d[o + 8] = colour.r; d[o + 9] = colour.g; d[o + 10] = colour.b; d[o + 11] = alpha;
    d[o + 12] = size; d[o + 13] = air ? 1 : 0;
  }

  /** An aura on the ground (a healer's reach, a shield wall), `radius` in world units. */
  aura(x: number, y: number, z: number, radius: number, colour: THREE.Color, alpha: number): void {
    const o = this.ground.next();
    if (o < 0) return;
    const d = this.ground.data;
    d[o] = x; d[o + 1] = y; d[o + 2] = z; d[o + 3] = 0;
    d[o + 8] = colour.r; d[o + 9] = colour.g; d[o + 10] = colour.b; d[o + 11] = alpha;
    d[o + 12] = radius * 2; d[o + 13] = 2;
  }

  /** A number that rises from a world point and fades (`+5` on a kill, `−2` on a leak): digits, `+` and `-` only. */
  float(text: string, at: THREE.Vector3, colour: THREE.Color, sizePx: number, life: number): void {
    let f = this.floaters.find((q) => !q.active);
    if (!f) f = this.floaters.reduce((a, b) => (a.age / a.life > b.age / b.life ? a : b));   // recycle the oldest
    f.active = true; f.text = text.slice(0, FLOATER_CHARS); f.x = at.x; f.y = at.y; f.z = at.z;
    f.r = colour.r; f.g = colour.g; f.b = colour.b; f.size = sizePx; f.age = 0; f.life = life;
  }

  /** Writes the floating numbers, then uploads both layers' live ranges. */
  end(dt: number): void {
    for (const f of this.floaters) {
      if (!f.active) continue;
      f.age += dt;
      if (f.age >= f.life) { f.active = false; continue; }
      const t = f.age / f.life;
      const alpha = t < FLOAT_FADE_FROM ? 1 : 1 - (t - FLOAT_FADE_FROM) / (1 - FLOAT_FADE_FROM);
      const rise = FLOAT_RISE_PX * (1 - (1 - t) * (1 - t));
      const pop = 1 + 0.35 * Math.max(0, 1 - t * 6);
      const size = f.size * pop, adv = size * CHAR_ADVANCE;
      const x0 = -adv * (f.text.length - 1) / 2;
      for (let i = 0; i < f.text.length; i++) {
        const cell = EnemyMarks.cellOf(f.text[i]);
        if (cell < 0) continue;
        const o = this.plates.next();
        if (o < 0) break;
        const d = this.plates.data;
        d[o] = f.x; d[o + 1] = f.y; d[o + 2] = f.z; d[o + 3] = 1;
        d[o + 4] = cell; d[o + 5] = 0; d[o + 6] = -1; d[o + 7] = -1;
        d[o + 8] = f.r; d[o + 9] = f.g; d[o + 10] = f.b; d[o + 11] = alpha;
        d[o + 12] = size; d[o + 13] = 0; d[o + 14] = x0 + i * adv; d[o + 15] = rise;
      }
    }
    this.plates.commit();
    this.ground.commit();
  }

  private static cellOf(ch: string): number {
    if (ch === "+") return PLUS_CELL;
    if (ch === "-") return MINUS_CELL;
    if (ch in LETTER_CELL) return LETTER_CELL[ch];
    const d = ch.charCodeAt(0) - 48;
    return d >= 0 && d <= 9 ? DIGIT_CELL + d : -1;
  }

  dispose(): void {
    this.plates.dispose(); this.ground.dispose();
    (this.plates.mesh.material as THREE.Material).dispose();
    (this.ground.mesh.material as THREE.Material).dispose();
  }
}
